import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Row, Col, Card, CardBody, Button, Table, Input, Label, Alert, Spinner,
  Modal, ModalHeader, ModalBody, ModalFooter, Badge, FormGroup, Pagination,
  PaginationItem, PaginationLink
} from 'reactstrap';
import Select from 'react-select';
import ApiService from '../services/ApiService';
import { toast } from 'react-toastify';
import VideoFrameTimeline from '../components/VideoFrameTimeline';

// Theme-aware styles for react-select
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--background-secondary)',
    borderColor: state.isFocused ? 'var(--accent-color)' : 'var(--border-color)',
    color: 'var(--text-primary)',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(var(--accent-color-rgb), 0.25)' : 'none',
    '&:hover': { borderColor: 'var(--accent-color)' }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--background-primary)',
    border: '1px solid var(--border-color)',
    zIndex: 9999
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'var(--accent-color)' : state.isFocused ? 'var(--hover-color)' : 'transparent',
    color: state.isSelected ? 'var(--background-primary)' : 'var(--text-primary)',
    '&:hover': { backgroundColor: state.isSelected ? 'var(--accent-color)' : 'var(--hover-color)' }
  }),
  singleValue: (provided) => ({ ...provided, color: 'var(--text-primary)' }),
  input: (provided) => ({ ...provided, color: 'var(--text-primary)' }),
  placeholder: (provided) => ({ ...provided, color: 'var(--text-secondary)' })
};

// Default categories - will be merged with any values found in actual data
const DEFAULT_PLACEMENT_CATEGORIES = [
  'athlete_jersey_front', 'athlete_jersey_arm', 'athlete_jersey_back',
  'athlete_shorts', 'athlete_caps',
  'stadium_pitch_board', 'stadium_led_board', 'stadium_signage',
  'field_surface', 'sports_equipment',
  'interview_backdrop', 'screen_overlay', 'other'
];

const DEFAULT_OBJECT_CATEGORIES = [
  'team', 'stadium', 'pitch', 'equipment', 'broadcast', 'officials', 'person', 'product', 'signage'
];

// Colors for bbox overlays (cycling through for different brands)
const BBOX_COLORS = [
  '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
  '#FF9F40', '#FF6384', '#C9CBCF', '#7BC8A4', '#E7E9ED'
];

const PlacementMappingReview = () => {
  // List state
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filter state
  const [filters, setFilters] = useState({
    rightsholder_id: '',
    logo_class: '',
    date_from: '',
    date_to: '',
    video_id: '',
    task_id: '',
    title: ''
  });

  // Dropdown data
  const [tenants, setTenants] = useState([]);
  const [brandClasses, setBrandClasses] = useState([]);

  // Modal placement filter
  const [modalObjectIdFilter, setModalObjectIdFilter] = useState('');
  const [framesByObjectId, setFramesByObjectId] = useState({});

  // Detail modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailPlacements, setDetailPlacements] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Edit state for overrides
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingOverride, setSavingOverride] = useState(false);

  // Bulk remap modal state
  const [bulkItem, setBulkItem] = useState(null);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [unmappedGroups, setUnmappedGroups] = useState([]);
  const [loadingUnmapped, setLoadingUnmapped] = useState(false);
  const [bulkForms, setBulkForms] = useState({});
  const [savingBulkRow, setSavingBulkRow] = useState(null);
  const [recomputingBulk, setRecomputingBulk] = useState(false);

  // Sponsor object cascade state (lazy-loaded per logo_class)
  const [sponsorObjectsByClass, setSponsorObjectsByClass] = useState({});
  const [loadingSponsorObjects, setLoadingSponsorObjects] = useState(false);

  // Bbox overlay state
  const [hoveredRow, setHoveredRow] = useState(null);
  const [showAllBboxes, setShowAllBboxes] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const imageRef = React.useRef(null);

  // Video frame navigation state
  const [videoFrames, setVideoFrames] = useState([]);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [isVideoTask, setIsVideoTask] = useState(false);
  const [loadingFramePlacements, setLoadingFramePlacements] = useState(false);



  // Load dropdown data
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [tenantsRes, brandsRes] = await Promise.all([
          ApiService.getAllTenants(),
          ApiService.getDetectionBrandClasses()
        ]);
        setTenants(tenantsRes.data);
        setBrandClasses(brandsRes.data);
      } catch (err) {
        console.error('Failed to load dropdown data:', err);
      }
    };
    loadDropdowns();
  }, []);

  const tenantOptions = useMemo(() =>
    tenants.map(t => ({ value: t.id, label: t.name })),
    [tenants]
  );

  const brandClassOptions = useMemo(() =>
    brandClasses.map(bc => ({ value: bc.class, label: bc.brand_name ? `${bc.class} (${bc.brand_name})` : bc.class })),
    [brandClasses]
  );

  // Build dynamic category lists: defaults + any values found in actual data
  const placementCategoryOptions = useMemo(() => {
    const fromData = detailPlacements.map(p => p.placement_category).filter(Boolean);
    return [...new Set([...DEFAULT_PLACEMENT_CATEGORIES, ...fromData])].sort();
  }, [detailPlacements]);

  const objectCategoryOptions = useMemo(() => {
    const fromData = detailPlacements.map(p => p.object_category).filter(Boolean);
    return [...new Set([...DEFAULT_OBJECT_CATEGORIES, ...fromData])].sort();
  }, [detailPlacements]);

  // Map brand classes to colors for bbox overlays
  const brandColorMap = useMemo(() => {
    const brands = [...new Set(detailPlacements.map(p => p.logo_class))];
    const map = {};
    brands.forEach((b, i) => { map[b] = BBOX_COLORS[i % BBOX_COLORS.length]; });
    return map;
  }, [detailPlacements]);

  // Frames filtered to only those containing the selected object ID
  const filteredFrames = useMemo(() => {
    if (!modalObjectIdFilter || !framesByObjectId[modalObjectIdFilter]) return videoFrames;
    const validNums = framesByObjectId[modalObjectIdFilter];
    return videoFrames.filter(f => validNums.has(f.frame_number));
  }, [videoFrames, modalObjectIdFilter, framesByObjectId]);

  // Dropdown options for object ID filter (only for video tasks)
  const frameObjectIdOptions = useMemo(() =>
    Object.keys(framesByObjectId).sort().map(id => ({
      value: id,
      label: `${id} (${framesByObjectId[id].size} frame${framesByObjectId[id].size !== 1 ? 's' : ''})`
    })),
    [framesByObjectId]
  );

  // Lazy-load sponsor objects for a given logo_class (cached by class)
  const loadSponsorObjectsForClass = useCallback(async (logoClass) => {
    if (sponsorObjectsByClass[logoClass]) return; // already loaded
    setLoadingSponsorObjects(true);
    try {
      const res = await ApiService.getAdminSponsorObjects({ per_page: 500 });
      const all = res.data.data || res.data;
      // Group all objects by logo_class via their brandMappings
      const byClass = {};
      all.forEach(obj => {
        (obj.brand_mappings || obj.brandMappings || []).forEach(m => {
          const cls = m.brand_class;
          if (!byClass[cls]) byClass[cls] = [];
          byClass[cls].push(obj);
        });
      });
      setSponsorObjectsByClass(prev => ({ ...prev, ...byClass }));
    } catch (err) {
      console.error('Failed to load sponsor objects', err);
    } finally {
      setLoadingSponsorObjects(false);
    }
  }, [sponsorObjectsByClass]);

  // Derived: rightsholders available for a logo_class
  const getRightsholderOptions = useCallback((logoClass) => {
    const objects = sponsorObjectsByClass[logoClass] || [];
    const seen = new Set();
    return objects
      .filter(o => {
        if (seen.has(o.rightsholder_id)) return false;
        seen.add(o.rightsholder_id);
        return true;
      })
      .map(o => ({
        value: o.rightsholder_id,
        label: o.rightsholder?.name || `Rightsholder #${o.rightsholder_id}`
      }));
  }, [sponsorObjectsByClass]);

  // Derived: sponsor objects for a logo_class + rightsholder
  const getSponsorObjectOptions = useCallback((logoClass, rightsholderIdStr) => {
    const rightsholderIdNum = parseInt(rightsholderIdStr, 10);
    const seen = new Set();
    return (sponsorObjectsByClass[logoClass] || [])
      .filter(o => {
        if (o.rightsholder_id !== rightsholderIdNum || seen.has(o.id)) return false;
        seen.add(o.id);
        return true;
      })
      .map(o => ({
        value: o.id,
        label: o.display_name || `${o.object_category} / ${o.object_id}`,
        object: o
      }));
  }, [sponsorObjectsByClass]);

  // Fetch analyses
  const fetchAnalyses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page: currentPage, per_page: 20 };
      if (filters.rightsholder_id) params.rightsholder_id = filters.rightsholder_id;
      if (filters.logo_class) params.logo_class = filters.logo_class;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.video_id) params.video_id = filters.video_id;
      if (filters.task_id) params.task_id = filters.task_id;
      if (filters.title) params.title = filters.title;

      const response = await ApiService.getPlacementAnalysis(params);
      setAnalyses(response.data.data);
      setTotalPages(response.data.last_page);
    } catch (err) {
      setError('Failed to load placement analysis: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  useEffect(() => {
    fetchAnalyses();
  }, [fetchAnalyses]);

  // Open detail modal
  const handleViewDetail = async (item) => {
    setSelectedItem(item);
    setIsDetailModalOpen(true);
    setLoadingDetail(true);
    setEditingRow(null);
    setHoveredRow(null);
    setShowAllBboxes(false);
    setModalObjectIdFilter('');
    setFramesByObjectId({});
    setImageSize({ width: 0, height: 0 });
    setNaturalSize({ width: 0, height: 0 });
    setVideoFrames([]);
    setCurrentFrameIndex(0);
    setIsVideoTask(false);

    try {
      // Fetch frames to detect video vs image
      const framesResponse = await ApiService.getPlacementFrames(item.video_id, item.task_id);
      const frames = framesResponse.data.frames || [];

      if (frames.length > 1) {
        // Video task - use frame navigation
        setIsVideoTask(true);
        setVideoFrames(frames);
        setCurrentFrameIndex(0);

        // Load placements for first frame (display) and all placements (frame index)
        const firstFrame = frames[0];
        const [detailResponse, allPlacementsResponse] = await Promise.all([
          ApiService.getPlacementAnalysisDetail(item.video_id, item.task_id, firstFrame.frame_number),
          ApiService.getPlacementAnalysisDetail(item.video_id, item.task_id)
        ]);
        setDetailPlacements(detailResponse.data);

        // Build object_id → Set<frame_number> index for frame filtering
        const fboid = {};
        (allPlacementsResponse.data || []).forEach(p => {
          const key = p.object_id || 'other';
          if (!fboid[key]) fboid[key] = new Set();
          fboid[key].add(p.frame_number);
        });
        setFramesByObjectId(fboid);
      } else {
        // Image task - load all placements (flat view)
        setIsVideoTask(false);
        const response = await ApiService.getPlacementAnalysisDetail(item.video_id, item.task_id);
        setDetailPlacements(response.data);
      }
    } catch (err) {
      toast.error('Failed to load placement details');
      setDetailPlacements([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Navigate to a different frame (video mode) — index is into filteredFrames
  const handleFrameChange = async (newIndex) => {
    if (!isVideoTask || !filteredFrames[newIndex]) return;
    setCurrentFrameIndex(newIndex);
    setEditingRow(null);
    setHoveredRow(null);
    setImageSize({ width: 0, height: 0 });
    setNaturalSize({ width: 0, height: 0 });

    const frame = filteredFrames[newIndex];
    setLoadingFramePlacements(true);
    try {
      const response = await ApiService.getPlacementAnalysisDetail(
        selectedItem.video_id, selectedItem.task_id, frame.frame_number
      );
      setDetailPlacements(response.data);
    } catch (err) {
      toast.error('Failed to load frame placements');
    } finally {
      setLoadingFramePlacements(false);
    }
  };

  // Change the object ID filter and navigate to first matching frame
  const handleModalFilterChange = async (opt) => {
    const newFilter = opt?.value || '';
    setModalObjectIdFilter(newFilter);
    setEditingRow(null);
    if (!isVideoTask) return;

    const frames = newFilter && framesByObjectId[newFilter]
      ? videoFrames.filter(f => framesByObjectId[newFilter].has(f.frame_number))
      : videoFrames;
    if (!frames[0]) return;
    setCurrentFrameIndex(0);
    setHoveredRow(null);
    setImageSize({ width: 0, height: 0 });
    setNaturalSize({ width: 0, height: 0 });
    setLoadingFramePlacements(true);
    try {
      const response = await ApiService.getPlacementAnalysisDetail(
        selectedItem.video_id, selectedItem.task_id, frames[0].frame_number
      );
      setDetailPlacements(response.data);
    } catch (err) {
      toast.error('Failed to load frame placements');
    } finally {
      setLoadingFramePlacements(false);
    }
  };

  // Keyboard navigation for video frames
  useEffect(() => {
    if (!isVideoTask || !isDetailModalOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleFrameChange(Math.max(0, currentFrameIndex - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleFrameChange(Math.min(filteredFrames.length - 1, currentFrameIndex + 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVideoTask, isDetailModalOpen, currentFrameIndex, filteredFrames.length]);

  // Start editing a row — also triggers lazy load of sponsor objects for this logo_class
  const handleEditRow = (placement, index) => {
    setEditingRow(index);
    setEditForm({
      rightsholder_id: placement.mapped_rightsholder_id || '',
      sponsor_object_id: '',
      placement_category: placement.placement_category || '',
      object_category: placement.object_category || '',
      object_id: placement.object_id || '',
      team_id: placement.team_id || '',
      mapped_tenant_id: placement.mapped_tenant_id || '',
      mapped_contract_id: placement.mapped_contract_id || '',
      mapped_rightsholder_id: placement.mapped_rightsholder_id || ''
    });
    loadSponsorObjectsForClass(placement.logo_class);
  };

  // When a sponsor object is chosen, auto-fill derived fields
  const handleSponsorObjectSelect = useCallback((opt, logoClass) => {
    if (!opt) {
      setEditForm(f => ({ ...f, sponsor_object_id: '', placement_category: '', object_category: '', object_id: '', team_id: '', mapped_tenant_id: '', mapped_rightsholder_id: f.rightsholder_id }));
      return;
    }
    const obj = opt.object;
    const mapping = (obj.brand_mappings || obj.brandMappings || []).find(m => m.brand_class === logoClass);
    setEditForm(f => ({
      ...f,
      sponsor_object_id: obj.id,
      placement_category: obj.placement_category || '',
      object_category: obj.object_category || '',
      object_id: obj.object_id || '',
      team_id: obj.team_id || '',
      mapped_tenant_id: mapping?.mapped_tenant_id || '',
      mapped_contract_id: mapping?.mapped_contract_id || '',
      mapped_rightsholder_id: obj.rightsholder_id || ''
    }));
  }, []);

  // Save override
  const handleSaveOverride = async (placement) => {
    setSavingOverride(true);
    try {
      await ApiService.overridePlacementMapping({
        video_id: placement.video_id,
        task_id: placement.task_id,
        logo_class: placement.logo_class,
        track_id: placement.track_id,
        frame_number: placement.frame_number,
        ...editForm,
        mapped_tenant_id: editForm.mapped_tenant_id || null,
        mapped_contract_id: editForm.mapped_contract_id || null,
        mapped_rightsholder_id: editForm.mapped_rightsholder_id || null
      });

      // Update local state
      setDetailPlacements(prev => prev.map((p, i) =>
        i === editingRow ? { ...p, ...editForm } : p
      ));
      setEditingRow(null);
      toast.success('Placement mapping updated');
    } catch (err) {
      toast.error('Failed to save override: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingOverride(false);
    }
  };

  // Recompute values
  const [recomputing, setRecomputing] = useState(false);
  const handleRecomputeValue = async () => {
    if (!selectedItem) return;
    setRecomputing(true);
    try {
      const res = await ApiService.recomputePlacementValue({
        task_id: selectedItem.task_id,
        video_id: selectedItem.video_id,
      });
      toast.success('Values recomputed successfully');
      if (res.data?.output) {
        console.log('Recompute output:', res.data.output);
      }
    } catch (err) {
      toast.error('Failed to recompute values: ' + (err.response?.data?.error || err.message));
    } finally {
      setRecomputing(false);
    }
  };

  // Open bulk remap modal for a list item
  const handleOpenBulkModal = async (item) => {
    setBulkItem(item);
    setIsBulkModalOpen(true);
    setUnmappedGroups([]);
    setBulkForms({});
    setLoadingUnmapped(true);
    try {
      const res = await ApiService.getUnmappedPlacements(item.video_id, item.task_id);
      const groups = res.data || [];
      setUnmappedGroups(groups);
      // Pre-populate bulk forms with existing rightsholder from data
      const forms = {};
      groups.forEach((g, i) => {
        forms[i] = {
          rightsholder_id: g.mapped_rightsholder_id || '',
          sponsor_object_id: '',
          placement_category: '',
          object_category: '',
          object_id: '',
          team_id: '',
          mapped_tenant_id: g.mapped_tenant_id || '',
          mapped_contract_id: '',
          mapped_rightsholder_id: g.mapped_rightsholder_id || '',
        };
        if (g.mapped_rightsholder_id || g.logo_class) {
          loadSponsorObjectsForClass(g.logo_class);
        }
      });
      setBulkForms(forms);
    } catch (err) {
      toast.error('Failed to load unmapped placements');
    } finally {
      setLoadingUnmapped(false);
    }
  };

  // Handle sponsor object selection in bulk form row
  const handleBulkSponsorObjectSelect = useCallback((opt, rowIndex, logoClass) => {
    if (!opt) {
      setBulkForms(prev => ({ ...prev, [rowIndex]: { ...prev[rowIndex], sponsor_object_id: '', placement_category: '', object_category: '', object_id: '', team_id: '', mapped_tenant_id: '', mapped_contract_id: '' } }));
      return;
    }
    const obj = opt.object;
    const mapping = (obj.brand_mappings || obj.brandMappings || []).find(m => m.brand_class === logoClass);
    setBulkForms(prev => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        sponsor_object_id: obj.id,
        placement_category: obj.placement_category || '',
        object_category: obj.object_category || '',
        object_id: obj.object_id || '',
        team_id: obj.team_id || '',
        mapped_tenant_id: mapping?.mapped_tenant_id || '',
        mapped_contract_id: mapping?.mapped_contract_id || '',
        mapped_rightsholder_id: obj.rightsholder_id || '',
      }
    }));
  }, []);

  // Save a single bulk remap row
  const handleBulkSave = async (group, rowIndex) => {
    const form = bulkForms[rowIndex];
    if (!form?.sponsor_object_id) return;
    setSavingBulkRow(rowIndex);
    try {
      await ApiService.bulkOverridePlacementMapping({
        video_id: bulkItem.video_id,
        task_id: bulkItem.task_id,
        logo_class: group.logo_class,
        match_placement_category: group.placement_category || null,
        match_object_id: group.object_id || null,
        placement_category: form.placement_category || null,
        object_category: form.object_category || null,
        object_id: form.object_id || null,
        team_id: form.team_id || null,
        mapped_tenant_id: form.mapped_tenant_id || null,
        mapped_contract_id: form.mapped_contract_id || null,
        mapped_rightsholder_id: form.mapped_rightsholder_id || null,
      });
      toast.success(`Remapped ${group.track_count} tracks for ${group.logo_class}`);
      // Remove from list since they're now mapped
      setUnmappedGroups(prev => prev.filter((_, i) => i !== rowIndex));
      setBulkForms(prev => {
        const next = { ...prev };
        delete next[rowIndex];
        return next;
      });
      setAnalyses(prev => prev.map(a =>
        a.video_id === bulkItem.video_id && a.task_id === bulkItem.task_id
          ? { ...a, unmapped_count: Math.max(0, (a.unmapped_count || 0) - group.track_count) }
          : a
      ));
    } catch (err) {
      toast.error('Bulk remap failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingBulkRow(null);
    }
  };

  // Recompute from bulk modal
  const handleRecomputeBulk = async () => {
    if (!bulkItem) return;
    setRecomputingBulk(true);
    try {
      await ApiService.recomputePlacementValue({ task_id: bulkItem.task_id, video_id: bulkItem.video_id });
      toast.success('Values recomputed successfully');
    } catch (err) {
      toast.error('Failed to recompute: ' + (err.response?.data?.error || err.message));
    } finally {
      setRecomputingBulk(false);
    }
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      rightsholder_id: '',
      logo_class: '',
      date_from: '',
      date_to: '',
      video_id: '',
      task_id: '',
      title: ''
    });
    setCurrentPage(1);
  };

  return (
    <>
      <Row>
        <Col xs={12}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-4" style={{ color: 'var(--text-primary)' }}>
                Placement Mapping Review
              </h5>

              {error && <Alert color="danger">{error}</Alert>}

              {/* Filters */}
              <Row className="mb-4">
                <Col md={3}>
                  <Label style={{ color: 'var(--text-primary)' }}>Rightsholder</Label>
                  <Select
                    options={tenantOptions}
                    value={tenantOptions.find(t => t.value === filters.rightsholder_id) || null}
                    onChange={(opt) => { setFilters(f => ({ ...f, rightsholder_id: opt?.value || '' })); setCurrentPage(1); }}
                    placeholder="All rightsholders..."
                    isClearable
                    styles={customSelectStyles}
                  />
                </Col>
                <Col md={3}>
                  <Label style={{ color: 'var(--text-primary)' }}>Brand</Label>
                  <Select
                    options={brandClassOptions}
                    value={brandClassOptions.find(bc => bc.value === filters.logo_class) || null}
                    onChange={(opt) => { setFilters(f => ({ ...f, logo_class: opt?.value || '' })); setCurrentPage(1); }}
                    placeholder="All brands..."
                    isClearable
                    styles={customSelectStyles}
                  />
                </Col>
                <Col md={2}>
                  <Label style={{ color: 'var(--text-primary)' }}>From</Label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => { setFilters(f => ({ ...f, date_from: e.target.value })); setCurrentPage(1); }}
                  />
                </Col>
                <Col md={2}>
                  <Label style={{ color: 'var(--text-primary)' }}>To</Label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => { setFilters(f => ({ ...f, date_to: e.target.value })); setCurrentPage(1); }}
                  />
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button color="secondary" size="sm" onClick={handleResetFilters}>
                    Reset
                  </Button>
                </Col>
              </Row>

              {/* Search by ID */}
              <Row className="mb-4">
                <Col md={4}>
                  <Label style={{ color: 'var(--text-primary)' }}>Video ID</Label>
                  <Input
                    type="text"
                    value={filters.video_id}
                    onChange={(e) => { setFilters(f => ({ ...f, video_id: e.target.value })); setCurrentPage(1); }}
                    placeholder="Filter by video ID..."
                  />
                </Col>
                <Col md={4}>
                  <Label style={{ color: 'var(--text-primary)' }}>Task ID</Label>
                  <Input
                    type="text"
                    value={filters.task_id}
                    onChange={(e) => { setFilters(f => ({ ...f, task_id: e.target.value })); setCurrentPage(1); }}
                    placeholder="Filter by task ID..."
                  />
                </Col>
                <Col md={4}>
                  <Label style={{ color: 'var(--text-primary)' }}>Media Title</Label>
                  <Input
                    type="text"
                    value={filters.title}
                    onChange={(e) => { setFilters(f => ({ ...f, title: e.target.value })); setCurrentPage(1); }}
                    placeholder="Search by media title..."
                  />
                </Col>
              </Row>

              {/* Results */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3" style={{ color: 'var(--text-secondary)' }}>Loading analyses...</p>
                </div>
              ) : analyses.length === 0 ? (
                <Alert color="info">No placement analyses found. Try adjusting your filters.</Alert>
              ) : (
                <>
                  <Table responsive hover className="align-middle">
                    <thead>
                      <tr>
                        <th style={{ color: 'var(--text-primary)' }}>Preview</th>
                        <th style={{ color: 'var(--text-primary)' }}>Video / Task</th>
                        <th style={{ color: 'var(--text-primary)' }}>Title</th>
                        <th style={{ color: 'var(--text-primary)' }}>Reach</th>
                        <th style={{ color: 'var(--text-primary)' }}>Date</th>
                        <th style={{ color: 'var(--text-primary)' }}>Brands</th>
                        <th style={{ color: 'var(--text-primary)' }}>Placements</th>
                        <th style={{ color: 'var(--text-primary)' }}>Count</th>
                        <th style={{ color: 'var(--text-primary)' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyses.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            {item.sample_image_url ? (
                              <img
                                src={item.sample_image_url}
                                alt="Preview"
                                style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                              />
                            ) : (
                              <div style={{ width: '80px', height: '60px', background: 'var(--background-secondary)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <small style={{ color: 'var(--text-secondary)' }}>No image</small>
                              </div>
                            )}
                          </td>
                          <td>
                            <div style={{ color: 'var(--text-primary)' }}>
                              <code style={{ fontSize: '0.8rem' }}>{item.video_id}</code>
                            </div>
                            <small style={{ color: 'var(--text-secondary)' }}>{item.task_id}</small>
                          </td>
                          <td style={{ color: 'var(--text-primary)', maxWidth: '220px' }}>
                            {item.title || '-'}
                          </td>
                          <td style={{ color: 'var(--text-secondary)' }}>
                            {item.reach != null ? item.reach.toLocaleString() : '-'}
                          </td>
                          <td style={{ color: 'var(--text-secondary)' }}>
                            {item.first_seen ? new Date(item.first_seen).toLocaleDateString() : '-'}
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {(item.brands || []).slice(0, 5).map((b, i) => (
                                <Badge key={i} color="primary" style={{ fontSize: '0.7rem' }}>{b}</Badge>
                              ))}
                              {(item.brands || []).length > 5 && (
                                <Badge color="secondary" style={{ fontSize: '0.7rem' }}>+{item.brands.length - 5}</Badge>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {(item.placements || []).slice(0, 3).map((p, i) => (
                                <Badge key={i} color="secondary" style={{ fontSize: '0.65rem' }}>{p}</Badge>
                              ))}
                              {(item.placements || []).length > 3 && (
                                <Badge color="secondary" style={{ fontSize: '0.65rem' }}>+{item.placements.length - 3}</Badge>
                              )}
                            </div>
                          </td>
                          <td>
                            <Badge color="info">{item.placement_count}</Badge>
                          </td>
                          <td>
                            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                              <Button color="primary" size="sm" onClick={() => handleViewDetail(item)}>
                                Review
                              </Button>
                              {item.unmapped_count > 0 && (
                                <Button
                                  color="warning"
                                  size="sm"
                                  onClick={() => handleOpenBulkModal(item)}
                                  title="Bulk remap unmapped detections"
                                >
                                  {item.unmapped_count} unmapped
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Pagination className="justify-content-center mt-3">
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink previous onClick={() => setCurrentPage(p => p - 1)} />
                      </PaginationItem>
                      {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                        const page = currentPage <= 5 ? i + 1
                          : currentPage >= totalPages - 4 ? totalPages - 9 + i
                          : currentPage - 5 + i;
                        if (page < 1 || page > totalPages) return null;
                        return (
                          <PaginationItem key={page} active={page === currentPage}>
                            <PaginationLink onClick={() => setCurrentPage(page)}>{page}</PaginationLink>
                          </PaginationItem>
                        );
                      })}
                      <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink next onClick={() => setCurrentPage(p => p + 1)} />
                      </PaginationItem>
                    </Pagination>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Detail Modal */}
      <Modal isOpen={isDetailModalOpen} toggle={() => setIsDetailModalOpen(false)} size="xl">
        <ModalHeader toggle={() => setIsDetailModalOpen(false)}>
          Placement Mappings: {selectedItem?.video_id}
        </ModalHeader>
        <ModalBody>
          {/* Video timeline (only for video tasks) */}
          {isVideoTask && videoFrames.length > 0 && (
            <VideoFrameTimeline
              frames={filteredFrames}
              currentFrameIndex={currentFrameIndex}
              onFrameChange={handleFrameChange}
              filterSlot={frameObjectIdOptions.length > 0 ? (
                <Select
                  options={frameObjectIdOptions}
                  value={frameObjectIdOptions.find(o => o.value === modalObjectIdFilter) || null}
                  onChange={handleModalFilterChange}
                  placeholder="All frames..."
                  isClearable
                  styles={{ ...customSelectStyles, container: (p) => ({ ...p, minWidth: '220px' }) }}
                />
              ) : null}
            />
          )}

          {/* Frame image with bbox overlays */}
          {(() => {
            // Determine image URL: for video, use current filtered frame's image; for images, use sample
            const currentFrameData = isVideoTask ? filteredFrames[currentFrameIndex] : null;
            const frameImageUrl = isVideoTask
              ? (currentFrameData?.image_url || currentFrameData?.debug_image_url)
              : selectedItem?.sample_image_url;

            if (!frameImageUrl) return null;

            return (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small style={{ color: 'var(--text-secondary)' }}>
                    {isVideoTask
                      ? 'Use ← → arrow keys to navigate frames. Hover a row to highlight its detection.'
                      : 'Hover a row to highlight its detection. Toggle "Show all" to see all bounding boxes.'}
                  </small>
                  <Button
                    color={showAllBboxes ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => setShowAllBboxes(!showAllBboxes)}
                  >
                    {showAllBboxes ? 'Hide all' : 'Show all'}
                  </Button>
                </div>
                <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                  <img
                    ref={imageRef}
                    src={frameImageUrl}
                    alt="Analysis frame"
                    style={{ maxWidth: '100%', maxHeight: '500px', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'block' }}
                    onLoad={() => {
                      if (imageRef.current) {
                        setImageSize({
                          width: imageRef.current.clientWidth,
                          height: imageRef.current.clientHeight
                        });
                        setNaturalSize({
                          width: imageRef.current.naturalWidth,
                          height: imageRef.current.naturalHeight
                        });
                      }
                    }}
                  />
                  {/* Bbox overlays */}
                  {imageSize.width > 0 && naturalSize.width > 0 && detailPlacements.map((p, idx) => {
                    const isHovered = hoveredRow === idx;
                    const shouldShow = showAllBboxes || isHovered;
                    if (!shouldShow) return null;
                    if (!p.bbox_x1 && !p.bbox_y1 && !p.bbox_x2 && !p.bbox_y2) return null;

                    const scaleX = imageSize.width / naturalSize.width;
                    const scaleY = imageSize.height / naturalSize.height;

                    const left = p.bbox_x1 * scaleX;
                    const top = p.bbox_y1 * scaleY;
                    const width = (p.bbox_x2 - p.bbox_x1) * scaleX;
                    const height = (p.bbox_y2 - p.bbox_y1) * scaleY;
                    const color = brandColorMap[p.logo_class] || '#FF6384';

                    return (
                      <div key={idx} style={{
                        position: 'absolute',
                        left: `${left}px`,
                        top: `${top}px`,
                        width: `${width}px`,
                        height: `${height}px`,
                        border: `2px solid ${color}`,
                        backgroundColor: `${color}22`,
                        borderRadius: '2px',
                        pointerEvents: 'none',
                        transition: 'opacity 0.15s',
                        opacity: isHovered ? 1 : 0.6
                      }}>
                        <span style={{
                          position: 'absolute',
                          top: '-18px',
                          left: '0',
                          fontSize: '0.65rem',
                          backgroundColor: color,
                          color: '#fff',
                          padding: '1px 4px',
                          borderRadius: '2px',
                          whiteSpace: 'nowrap'
                        }}>
                          {p.logo_class}{p.placement_category ? ` · ${p.placement_category}` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {(loadingDetail || loadingFramePlacements) ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <span className="ms-2" style={{ color: 'var(--text-secondary)' }}>Loading placements...</span>
            </div>
          ) : detailPlacements.length === 0 ? (
            <Alert color="info">No placement data found.</Alert>
          ) : (() => {
            // For image tasks: filter table rows by object ID (dropdown shown here).
            // For video tasks: frame navigation already handles filtering; show all rows for current frame.
            const objectIdOptions = !isVideoTask
              ? [
                  ...[...new Set(detailPlacements.map(p => p.object_id).filter(Boolean))].sort().map(id => ({ value: id, label: id })),
                  ...(detailPlacements.some(p => !p.object_id) ? [{ value: 'other', label: 'other' }] : [])
                ]
              : [];
            const visiblePlacements = (!isVideoTask && modalObjectIdFilter)
              ? detailPlacements.filter(p => modalObjectIdFilter === 'other' ? !p.object_id : p.object_id === modalObjectIdFilter)
              : detailPlacements;
            return (
            <div style={{ overflowX: 'auto' }}>
              {objectIdOptions.length > 0 && (
                <div className="mb-3" style={{ maxWidth: '280px' }}>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filter by Object ID</label>
                  <Select
                    options={objectIdOptions}
                    value={objectIdOptions.find(o => o.value === modalObjectIdFilter) || null}
                    onChange={handleModalFilterChange}
                    placeholder="All object IDs..."
                    isClearable
                    styles={customSelectStyles}
                  />
                </div>
              )}
              <Table responsive hover size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Brand</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Track</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Placement</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Object Cat.</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Object ID</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Team</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Tenant</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Duration</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePlacements.map((p, idx) => {
                    const isEditing = editingRow === idx;
                    const rowColor = brandColorMap[p.logo_class] || '#FF6384';
                    return (
                      <React.Fragment key={idx}>
                        {/* Read-only row */}
                        <tr
                          onMouseEnter={() => setHoveredRow(idx)}
                          onMouseLeave={() => setHoveredRow(null)}
                          style={{
                            backgroundColor: isEditing
                              ? 'var(--background-secondary)'
                              : hoveredRow === idx ? 'var(--hover-color)' : 'transparent',
                            transition: 'background-color 0.15s'
                          }}
                        >
                          <td>
                            <Badge style={{ fontSize: '0.75rem', backgroundColor: rowColor, border: 'none' }}>
                              {p.logo_class}
                            </Badge>
                          </td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{p.track_id}</td>
                          <td>
                            <Badge color="secondary" style={{ fontSize: '0.7rem' }}>
                              {p.placement_category || '-'}
                            </Badge>
                          </td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{p.object_category || '-'}</td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{p.object_id || '-'}</td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{p.team_id || '-'}</td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                            {p.mapped_tenant_id ? (tenants.find(t => t.id === p.mapped_tenant_id)?.name || `#${p.mapped_tenant_id}`) : '-'}
                          </td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                            {p.track_duration_seconds ? `${p.track_duration_seconds.toFixed(1)}s` : '-'}
                          </td>
                          <td>
                            {!isEditing && (
                              <Button color="info" size="sm" onClick={() => handleEditRow(p, idx)}>Edit</Button>
                            )}
                          </td>
                        </tr>

                        {/* Inline edit panel — full-width row beneath */}
                        {isEditing && (() => {
                          const rhOptions = getRightsholderOptions(p.logo_class);
                          const soOptions = editForm.rightsholder_id
                            ? getSponsorObjectOptions(p.logo_class, editForm.rightsholder_id)
                            : [];
                          const tenantOpt = tenantOptions.find(t => t.value === editForm.mapped_tenant_id) || null;
                          const portalStyles = { ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 99999 }) };
                          return (
                            <tr>
                              <td colSpan={9} style={{ padding: '0', borderTop: `2px solid ${rowColor}` }}>
                                <div style={{
                                  backgroundColor: 'var(--background-secondary)',
                                  padding: '16px 20px',
                                  borderBottom: '1px solid var(--border-color)'
                                }}>
                                  {/* Row 1: Selects */}
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '16px', marginBottom: '12px' }}>
                                    <div>
                                      <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rightsholder</label>
                                      {loadingSponsorObjects ? <Spinner size="sm" /> : (
                                        <Select
                                          options={rhOptions}
                                          value={rhOptions.find(o => String(o.value) === String(editForm.rightsholder_id)) || null}
                                          onChange={(opt) => setEditForm(f => ({ ...f, rightsholder_id: opt?.value || '', sponsor_object_id: '', placement_category: '', object_category: '', object_id: '', team_id: '', mapped_tenant_id: '', mapped_contract_id: '' }))}
                                          placeholder={rhOptions.length === 0 ? 'No matches' : 'Select...'}
                                          isClearable
                                          menuPortalTarget={document.body}
                                          styles={portalStyles}
                                        />
                                      )}
                                    </div>
                                    <div>
                                      <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sponsor Object</label>
                                      <Select
                                        options={soOptions}
                                        value={soOptions.find(o => o.value === editForm.sponsor_object_id) || null}
                                        onChange={(opt) => handleSponsorObjectSelect(opt, p.logo_class)}
                                        placeholder={editForm.rightsholder_id ? 'Select object...' : 'Select rightsholder first'}
                                        isDisabled={!editForm.rightsholder_id}
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={portalStyles}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tenant</label>
                                      <Select
                                        options={tenantOptions}
                                        value={tenantOpt}
                                        onChange={(opt) => setEditForm(f => ({ ...f, mapped_tenant_id: opt?.value || '' }))}
                                        placeholder="Tenant..."
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={portalStyles}
                                      />
                                    </div>
                                  </div>

                                  {/* Row 2: Auto-filled fields + Save */}
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                      {[
                                        ['Placement', editForm.placement_category],
                                        ['Object Cat.', editForm.object_category],
                                        ['Object ID', editForm.object_id],
                                        ['Team', editForm.team_id],
                                      ].map(([label, val]) => (
                                        <div key={label}>
                                          <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{label}</div>
                                          <div style={{ fontSize: '0.85rem', color: val ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: val ? 500 : 400 }}>{val || '—'}</div>
                                        </div>
                                      ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                      <Button color="secondary" size="sm" onClick={() => setEditingRow(null)}>Cancel</Button>
                                      <Button color="success" size="sm"
                                        onClick={() => handleSaveOverride(p)}
                                        disabled={savingOverride || !editForm.sponsor_object_id}>
                                        {savingOverride ? <Spinner size="sm" /> : 'Save'}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })()}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            );
          })()}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button color="warning" onClick={handleRecomputeValue} disabled={recomputing}>
            {recomputing ? <><Spinner size="sm" className="me-1" /> Recomputing...</> : 'Recompute Values'}
          </Button>
          <Button color="secondary" onClick={() => setIsDetailModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk Remap Modal */}
      <Modal isOpen={isBulkModalOpen} toggle={() => setIsBulkModalOpen(false)} size="xl">
        <ModalHeader toggle={() => setIsBulkModalOpen(false)}>
          Bulk Remap Unmapped Detections: {bulkItem?.video_id}
        </ModalHeader>
        <ModalBody>
          {loadingUnmapped ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <span className="ms-2" style={{ color: 'var(--text-secondary)' }}>Loading unmapped detections...</span>
            </div>
          ) : unmappedGroups.length === 0 ? (
            <Alert color="success">All detections are mapped.</Alert>
          ) : (
            <>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '16px' }}>
                {unmappedGroups.reduce((sum, g) => sum + (g.track_count || 0), 0)} tracks across {unmappedGroups.length} group{unmappedGroups.length !== 1 ? 's' : ''} have no sponsor object assigned. Select the correct sponsor object for each group and save.
              </p>
              <Table responsive size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Brand</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Current Placement</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>Tracks</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem', minWidth: '480px' }}>Remap to</th>
                    <th style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {unmappedGroups.map((group, rowIndex) => {
                    const form = bulkForms[rowIndex] || {};
                    const rhOptions = getRightsholderOptions(group.logo_class);
                    const soOptions = form.rightsholder_id
                      ? getSponsorObjectOptions(group.logo_class, form.rightsholder_id)
                      : [];
                    const portalStyles = { ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 99999 }) };
                    return (
                      <tr key={rowIndex}>
                        <td>
                          <Badge style={{ fontSize: '0.75rem', backgroundColor: '#6c757d', border: 'none' }}>
                            {group.logo_class}
                          </Badge>
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                          {group.placement_category || '—'}
                        </td>
                        <td>
                          <Badge color="warning" style={{ fontSize: '0.75rem' }}>{group.track_count}</Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-2 align-items-start flex-wrap">
                            <div style={{ minWidth: '160px', flex: '1 1 160px' }}>
                              <label style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rightsholder</label>
                              {loadingSponsorObjects ? <Spinner size="sm" /> : (
                                <Select
                                  options={rhOptions}
                                  value={rhOptions.find(o => String(o.value) === String(form.rightsholder_id)) || null}
                                  onChange={(opt) => {
                                    setBulkForms(prev => ({ ...prev, [rowIndex]: { ...prev[rowIndex], rightsholder_id: opt?.value || '', sponsor_object_id: '', placement_category: '', object_category: '', object_id: '', team_id: '', mapped_tenant_id: '', mapped_contract_id: '' } }));
                                  }}
                                  placeholder={rhOptions.length === 0 ? 'No matches' : 'Select...'}
                                  isClearable
                                  menuPortalTarget={document.body}
                                  styles={portalStyles}
                                />
                              )}
                            </div>
                            <div style={{ minWidth: '200px', flex: '2 1 200px' }}>
                              <label style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sponsor Object</label>
                              <Select
                                options={soOptions}
                                value={soOptions.find(o => o.value === form.sponsor_object_id) || null}
                                onChange={(opt) => handleBulkSponsorObjectSelect(opt, rowIndex, group.logo_class)}
                                placeholder={form.rightsholder_id ? 'Select object...' : 'Select rightsholder first'}
                                isDisabled={!form.rightsholder_id}
                                isClearable
                                menuPortalTarget={document.body}
                                styles={portalStyles}
                              />
                            </div>
                            {form.sponsor_object_id && (
                              <div style={{ display: 'flex', gap: '12px', alignSelf: 'center', flexWrap: 'wrap' }}>
                                {[['Placement', form.placement_category], ['Obj cat.', form.object_category], ['Object ID', form.object_id], ['Team', form.team_id]].map(([label, val]) => (
                                  <div key={label}>
                                    <div style={{ fontSize: '0.6rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-primary)', fontWeight: 500 }}>{val || '—'}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handleBulkSave(group, rowIndex)}
                            disabled={!form.sponsor_object_id || savingBulkRow === rowIndex}
                          >
                            {savingBulkRow === rowIndex ? <Spinner size="sm" /> : `Remap all ${group.track_count}`}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button color="warning" onClick={handleRecomputeBulk} disabled={recomputingBulk}>
            {recomputingBulk ? <><Spinner size="sm" className="me-1" /> Recomputing...</> : 'Recompute Values'}
          </Button>
          <Button color="secondary" onClick={() => setIsBulkModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PlacementMappingReview;
